import type { LobKey } from "src/jsonForms/preScreen/types";
import { MetricsPublisher } from "src/metrics/metrics";

import { validateSearchConfig, type ValidationError } from "./configValidator";
import { routeLineOfBusiness, SEARCH_CONFIG_REGISTRY } from "./registry";
import { searchConfigSource, SearchConfigMissingError } from "./searchConfigSource";
import type { SearchConfig } from "./types";

export type SearchConfigFailure =
  | { readonly kind: "unknownRoute"; readonly pathname: string }
  | { readonly kind: "missingConfig"; readonly lob: LobKey }
  | {
      readonly kind: "invalidConfig";
      readonly lob: LobKey;
      readonly errors: readonly ValidationError[];
    }
  | { readonly kind: "retrieval"; readonly lob: LobKey };

export type SearchConfigOutcome =
  | { readonly ok: true; readonly lob: LobKey; readonly config: SearchConfig }
  | { readonly ok: false; readonly failure: SearchConfigFailure };

/**
 * Validated configs, keyed by line of business, with no TTL and no
 * invalidation. A bundled document cannot change without a deployment, and a
 * deployment discards this module's state, so what the memo avoids is
 * re-running the validator on every mount and route change. It is also what
 * makes idempotent resolution structural: the second resolution of the same
 * route returns the identical object, so the column set and order cannot drift.
 *
 * Only successes are stored, so a failure is never replayed from the memo.
 */
const memoizedConfigs = new Map<LobKey, SearchConfig>();

/** Clears the memo. For tests, so one case does not inherit another's memo. */
export function clearSearchConfigMemo(): void {
  memoizedConfigs.clear();
}

/**
 * Resolves the Search_Config for a claims history route.
 *
 * Never throws. An unregistered route, an unregistered line of business, a
 * document the validator rejects, and a source that fails all return an
 * outcome, so a configuration problem cannot take the page down.
 *
 * Publishes exactly one retrieval metric per terminal outcome, including the
 * memo hit and including every failure. The one exception is an unregistered
 * route, which resolves no line of business and attempts no retrieval, leaving
 * the metric nothing to record.
 *
 * Asynchronous although the source is bundled: the timeout, retry, and
 * cancellation behaviour that a backend retrieval needs is added here with the
 * operation, and this signature is what lets that arrive without reaching into
 * the page.
 */
export async function resolveSearchConfig(pathname: string): Promise<SearchConfigOutcome> {
  const lob = routeLineOfBusiness(pathname);
  if (lob === null) {
    return { ok: false, failure: { kind: "unknownRoute", pathname } };
  }

  const startedAt = Date.now();
  try {
    return await resolveForLineOfBusiness(lob, startedAt);
  } catch (error) {
    if (error instanceof SearchConfigMissingError) {
      return reportFailure(lob, startedAt, { kind: "missingConfig", lob: error.lob });
    }
    // The never-throws guarantee is structural rather than inherited from the
    // collaborators: a defect in the source or the validator still leaves the
    // page with a reported failure instead of an unhandled rejection.
    console.error(error);
    return reportFailure(lob, startedAt, { kind: "retrieval", lob });
  }
}

async function resolveForLineOfBusiness(
  lob: LobKey,
  startedAt: number
): Promise<SearchConfigOutcome> {
  const memoized = memoizedConfigs.get(lob);
  if (memoized) {
    publishRetrievalMetric(lob, "success", startedAt, memoized);
    return { ok: true, lob, config: memoized };
  }

  const validation = validateSearchConfig(await searchConfigSource.load(lob));
  if (!validation.ok) {
    return reportFailure(lob, startedAt, { kind: "invalidConfig", lob, errors: validation.errors });
  }

  memoizedConfigs.set(lob, validation.config);
  publishRetrievalMetric(lob, "success", startedAt, validation.config);
  return { ok: true, lob, config: validation.config };
}

type RetrievalOutcomeLabel = "success" | "failure";

function reportFailure(
  lob: LobKey,
  startedAt: number,
  failure: SearchConfigFailure
): SearchConfigOutcome {
  publishRetrievalMetric(lob, "failure", startedAt);
  return { ok: false, failure };
}

/**
 * Declared by every shipped config, so it only applies where no document is
 * reachable to declare it — a missing registry entry, or a source that failed
 * before returning anything.
 */
const FALLBACK_RETRIEVAL_PUBLISHER_NAME = "claimsHistory.resolveSearchConfig";

/**
 * One timer per terminal outcome. The publisher name is the namespace and the
 * metric name carries the line of business and the outcome, because a timer
 * publishes a duration and nothing else.
 *
 * Wrapped, because publishing is fire-and-forget: a metrics outage must not
 * present as a claims outage, and must not turn a resolved config into a
 * failure.
 */
function publishRetrievalMetric(
  lob: LobKey,
  outcome: RetrievalOutcomeLabel,
  startedAt: number,
  config?: SearchConfig
): void {
  const durationMs = Date.now() - startedAt;
  try {
    const publisherName =
      config?.metrics.configRetrievalPublisherName ??
      SEARCH_CONFIG_REGISTRY[lob]?.searchConfig.metrics.configRetrievalPublisherName ??
      FALLBACK_RETRIEVAL_PUBLISHER_NAME;
    new MetricsPublisher(publisherName).publishNamedTimerMetric(`${lob}.${outcome}`, durationMs);
  } catch (error) {
    console.error(error);
  }
}
